import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sun, Moon, Monitor } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/renderer/components/ui/select'
import { SegmentedControl } from '@/src/renderer/components/ui/SegmentedControl'
import { Switch } from '@/src/renderer/components/ui/switch'
import { useMode, type Mode, type Theme } from '@/src/renderer/hooks/useMode'
import { useLanguage } from '@/src/renderer/contexts/LanguageProvider'
import { useDisplayMode, type DisplayMode } from '@/src/renderer/contexts/DisplayModeProvider'
import { getAnalyticsConsent, setAnalyticsConsent } from '@/src/renderer/lib/analytics'
import themesConfig from '@/src/renderer/styles/themes.json'
import { SettingsSection, SettingsRow, SettingsResetCard } from '../../primitives'
import { AccountProfileSection } from '../../AccountProfileSection'

interface AppearancePageProps {
  /** Host platform — Electron-only sections (Dock/Menubar/Tray) are
   *  conditionally rendered when `platform === 'electron'`. */
  platform: 'electron' | 'web'
}

/**
 * The "Erscheinungsbild" SubPage under "App & Bedienung". Initially hosts
 * ALL existing settings content (Profile + Mode + Palette + Language + App
 * Behavior + Privacy) using the new SettingsSection/Row pattern; later
 * phases will move individual sections to their proper SubPages (e.g.
 * Profile → "Konto & Profil", Privacy → "Daten & Privatsphäre").
 */
export function AppearancePage({ platform }: AppearancePageProps) {
  const { t } = useTranslation()

  return (
    <div className="appearance-page">
      <AccountProfileSection />
      <DisplaySection />
      <LanguageSection />
      {platform === 'electron' && <AppBehaviorSection />}
      <PrivacySection />
      <SettingsResetCard
        description={t('settings.reset.description')}
        buttonLabel={t('settings.reset.button')}
        disabled
      />
    </div>
  )
}

// ----- Display (Mode + Palette) ----------------------------------------------

const importedThemes = themesConfig.themes as Array<{ id: string; name: string }>

function DisplaySection() {
  const { t } = useTranslation()
  const { mode, setMode, theme, setTheme } = useMode()

  return (
    <SettingsSection title={t('settings.section.display')}>
      <SettingsRow
        label={t('preferences.appearance.theme')}
        description={
          mode === 'system'
            ? t('preferences.appearance.systemDescription')
            : t('preferences.appearance.fixedDescription', { theme: t(`preferences.appearance.${mode}`).toLowerCase() })
        }
        control={
          <SegmentedControl<Mode>
            value={mode}
            onValueChange={setMode}
            aria-label={t('preferences.appearance.theme')}
            options={[
              { value: 'light', label: t('preferences.appearance.light'), icon: <Sun size={16} /> },
              { value: 'dark', label: t('preferences.appearance.dark'), icon: <Moon size={16} /> },
              { value: 'system', label: t('preferences.appearance.system'), icon: <Monitor size={16} /> },
            ]}
          />
        }
      />
      <SettingsRow
        label={t('preferences.appearance.palette')}
        description={t('preferences.appearance.paletteDescription')}
        htmlFor="appearance-palette-select"
        control={
          <Select value={theme} onValueChange={(v) => setTheme(v as Theme)}>
            <SelectTrigger id="appearance-palette-select" className="w-[200px]">
              <SelectValue>
                {theme === 'standard'
                  ? t('preferences.appearance.paletteStandard')
                  : importedThemes.find((p) => p.id === theme)?.name ?? t('preferences.appearance.paletteStandard')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {/* "Standard" is the implicit baseline palette and is NOT in
                  themes.json — render it as a fixed first option, mirroring
                  the pre-redesign AppearanceSection. */}
              <SelectItem value="standard">{t('preferences.appearance.paletteStandard')}</SelectItem>
              {importedThemes.map((palette) => (
                <SelectItem key={palette.id} value={palette.id}>
                  {palette.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
    </SettingsSection>
  )
}

// ----- Language --------------------------------------------------------------

function LanguageSection() {
  const { t } = useTranslation()
  const { language, changeLanguage } = useLanguage()

  return (
    <SettingsSection title={t('settings.section.language')}>
      <SettingsRow
        label={t('preferences.language.title')}
        description={t('preferences.language.changesImmediate')}
        control={
          <SegmentedControl
            value={language}
            onValueChange={changeLanguage}
            aria-label={t('language.select')}
            options={[
              { value: 'de', label: t('language.german') },
              { value: 'en', label: t('language.english') },
            ]}
          />
        }
      />
    </SettingsSection>
  )
}

// ----- App Behavior (Electron-only) ------------------------------------------

function AppBehaviorSection() {
  const { t } = useTranslation()
  const { displayMode, platformModes, setDisplayMode, isLoading } = useDisplayMode()
  const isMac = platformModes.modes.includes('dock-only')
  const prefix = isMac
    ? 'preferences.appBehavior.displayModes.mac'
    : 'preferences.appBehavior.displayModes.windows'
  const options = [
    { value: platformModes.modes[0] as DisplayMode, label: t(`${prefix}.${isMac ? 'dockOnly' : 'taskbarOnly'}`) },
    { value: platformModes.modes[1] as DisplayMode, label: t(`${prefix}.${isMac ? 'menubarOnly' : 'systrayOnly'}`) },
    { value: platformModes.modes[2] as DisplayMode, label: t(`${prefix}.both`) },
  ]

  return (
    <SettingsSection title={t('settings.section.appBehavior')}>
      <SettingsRow
        label={t('preferences.appBehavior.displayMode')}
        description={t('preferences.appBehavior.changesImmediate')}
        htmlFor="appearance-displaymode-select"
        control={
          isLoading ? (
            <div className="w-[280px] h-9 bg-muted animate-pulse rounded-md" aria-hidden="true" />
          ) : (
            <Select value={displayMode} onValueChange={(v) => setDisplayMode(v as DisplayMode)}>
              <SelectTrigger id="appearance-displaymode-select" className="w-[280px]">
                <SelectValue>{options.find((o) => o.value === displayMode)?.label}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }
      />
    </SettingsSection>
  )
}

// ----- Privacy ---------------------------------------------------------------

function PrivacySection() {
  const { t } = useTranslation()
  const [enabled, setEnabled] = useState(() => getAnalyticsConsent())

  return (
    <SettingsSection title={t('settings.section.privacy')}>
      <SettingsRow
        label={t('preferences.privacy.analyticsTitle')}
        description={t('preferences.privacy.analyticsDescription')}
        htmlFor="appearance-analytics-toggle"
        control={
          <Switch
            id="appearance-analytics-toggle"
            checked={enabled}
            onCheckedChange={(checked) => {
              setEnabled(checked)
              setAnalyticsConsent(checked)
            }}
          />
        }
      />
    </SettingsSection>
  )
}
